import { BrowserRouter as Router, Routes, Route, Navigate, useNavigationType, useLocation } from "react-router-dom";
import "./App.css";
import React, { useEffect } from "react";

const CareersPage1 = React.lazy(() => import("./pages/CareersPage1"));
const CareersPage = React.lazy(() => import("./pages/CareersPage"));

const ScrollRestorer = () => {
  const action = useNavigationType();
  const { pathname, hash } = useLocation();

  // Scroll to top on new navigations (except browser back/forward)
  useEffect(() => {
    if (action !== "POP") {
      window.scrollTo(0, 0);
    }
  }, [action, pathname]);

  // Hash-link support for internal navigation
  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        const el = document.getElementById(hash.slice(1));
        if (el) {
          const headerOffset = 160;
          const elementPosition = el.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [hash]);

  // Dynamic SEO meta tags for each route
  useEffect(() => {
    let title = "";
    let metaDescription = "";
    let canonicalUrl = "";

    switch (pathname) {
      case "/":
        title = "Careers at TradeIT Solutions - Join the Future of Trading Education";
        metaDescription = 
          "Join TradeIT Solutions and shape the future of trading education. Explore remote opportunities in operations, education, marketing, content creation, and design. Contract-to-hire positions with growth potential.";
        canonicalUrl = "https://careers.tradeitsolutions.com/";
        break;
      default:
        if (pathname.startsWith("/careers/")) {
          const jobSlug = pathname.split("/careers/")[1];
          const jobTitleMap = {
            "trading-instructor": "Trading Professor",
            "admin-assistant": "Admin Assistant",
            "instructors-assistant": "Professor's Assistant",
            "engagement-manager": "Engagement Manager",
            "head-of-marketing": "Marketing Lead",
            "cinematic-video-editor": "Cinematic Video Editor",
            "social-media-manager": "Social Media Manager",
            "graphic-designer": "Graphic Designer",
            "ui-ux-designer": "UI/UX Designer"
          };
          
          const jobTitle = jobTitleMap[jobSlug] || "Career Opportunity";
          title = `${jobTitle} - Careers at TradeIT Solutions`;
          metaDescription = 
            `Join TradeIT Solutions as a ${jobTitle}. Remote position with competitive compensation, growth opportunities, and the chance to impact the future of trading education. Apply now.`;
          canonicalUrl = `https://careers.tradeitsolutions.com/careers/${jobSlug}`;
        } else {
          title = "TradeIT Solutions - Elite Trading Education Platform";
          metaDescription = 
            "TradeIT Solutions combines university-level trading education with digital innovation. Structured programs, expert mentorship, and real-world application.";
          canonicalUrl = "https://careers.tradeitsolutions.com/";
        }
    }

    document.title = title;

    let descriptionTag = document.querySelector('head > meta[name="description"]');
    if (!descriptionTag) {
      descriptionTag = document.createElement("meta");
      descriptionTag.name = "description";
      document.head.appendChild(descriptionTag);
    }
    descriptionTag.content = metaDescription;

    let canonicalTag = document.querySelector('head > link[rel="canonical"]');
    if (!canonicalTag) {
      canonicalTag = document.createElement("link");
      canonicalTag.rel = "canonical";
      document.head.appendChild(canonicalTag);
    }
    canonicalTag.href = canonicalUrl;

    const ogTags = [
      { property: "og:title", content: title },
      { property: "og:description", content: metaDescription },
      { property: "og:url", content: canonicalUrl },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "TradeIT Solutions Careers" },
      { property: "og:image", content: "https://careers.tradeitsolutions.com/og-careers.jpg" },
      { property: "og:image:alt", content: "Join TradeIT Solutions - Elite Trading Education Careers" }
    ];

    ogTags.forEach(({ property, content }) => {
      let tag = document.querySelector(`head > meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });

    const twitterTags = [
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@TradeITSolutions" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: metaDescription },
      { name: "twitter:image", content: "https://careers.tradeitsolutions.com/twitter-careers.jpg" }
    ];

    twitterTags.forEach(({ name, content }) => {
      let tag = document.querySelector(`head > meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.name = name;
        document.head.appendChild(tag);
      }
      tag.content = content;
    });

  }, [pathname]);

  return null;
};

const SmoothScrollEnhancer = () => {
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      html {
        scroll-behavior: smooth;
        scroll-padding-top: 160px;
      }
      
      ::-webkit-scrollbar {
        width: 8px;
      }
      
      ::-webkit-scrollbar-track {
        background: #2c2c48;
        border-radius: 4px;
      }
      
      ::-webkit-scrollbar-thumb {
        background: linear-gradient(135deg, #585def 0%, #6d51b0 50%, #e194f4 100%);
        border-radius: 4px;
        transition: background 0.3s ease;
      }
      
      ::-webkit-scrollbar-thumb:hover {
        background: linear-gradient(135deg, #6d65f0 0%, #7d61c0 50%, #f1a4f4 100%);
      }
      
      * {
        scrollbar-width: thin;
        scrollbar-color: #585def #2c2c48;
      }
      
      @media (max-width: 768px) {
        html {
          -webkit-overflow-scrolling: touch;
          scroll-padding-top: 120px;
        }
        
        ::-webkit-scrollbar {
          width: 4px;
        }
      }
      
      body {
        overflow-x: hidden;
        max-width: 100vw;
      }
      
      .skip-to-content {
        position: absolute;
        top: -40px;
        left: 6px;
        background: #585def;
        color: white;
        padding: 8px;
        text-decoration: none;
        border-radius: 4px;
        z-index: 10000;
        transition: top 0.3s;
      }
      
      .skip-to-content:focus {
        top: 6px;
      }
      
      @media (prefers-contrast: high) {
        ::-webkit-scrollbar-thumb {
          background: #ffffff;
        }
      }
      
      @media (prefers-reduced-motion: reduce) {
        html {
          scroll-behavior: auto;
        }
        
        * {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.01ms !important;
        }
      }
    `;
    document.head.appendChild(style);

    window.smoothScrollTo = (target, duration = 800, offset = 160) => {
      const element = typeof target === 'string'
        ? document.querySelector(target)
        : target;

      if (!element) return;

      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      const startPosition = window.pageYOffset;
      const distance = offsetPosition - startPosition;
      let startTime = null;

      const animation = (currentTime) => {
        if (startTime === null) startTime = currentTime;
        const timeElapsed = currentTime - startTime;
        const run = easeInOutCubic(timeElapsed, startPosition, distance, duration);
        window.scrollTo(0, run);
        if (timeElapsed < duration) requestAnimationFrame(animation);
      };

      const easeInOutCubic = (t, b, c, d) => {
        t /= d / 2;
        if (t < 1) return c / 2 * t * t * t + b;
        t -= 2;
        return c / 2 * (t * t * t + 2) + b;
      };

      requestAnimationFrame(animation);
    };

    const handleAnchorClick = (e) => {
      const anchor = e.target.closest('a[href^="#"]');
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (href === '#' || href === '#top') {
        e.preventDefault();
        window.smoothScrollTo(document.body, 800, 0);
        return;
      }

      e.preventDefault();
      const target = document.querySelector(href);
      if (target) {
        window.smoothScrollTo(target);
        window.history.pushState(null, null, href);
      }
    };

    document.addEventListener('click', handleAnchorClick);

    const skipLink = document.createElement('a');
    skipLink.href = '#main-content';
    skipLink.className = 'skip-to-content';
    skipLink.textContent = 'Skip to main content';
    document.body.insertBefore(skipLink, document.body.firstChild);

    return () => {
      document.removeEventListener('click', handleAnchorClick);
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
      if (skipLink.parentNode) {
        skipLink.parentNode.removeChild(skipLink);
      }
    };
  }, []);

  return null;
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Career page error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: '#2c2c48',
          color: '#ffffff',
          textAlign: 'center',
          padding: '20px'
        }}>
          <h1 style={{ color: '#585def', marginBottom: '16px' }}>
            Something went wrong
          </h1>
          <p style={{ marginBottom: '24px', color: '#cbd5e1' }}>
            We're sorry, but there was an error loading the careers page.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: 'linear-gradient(135deg, #585def 0%, #6d51b0 50%, #e194f4 100%)',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Refresh Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

function App() {
  useEffect(() => {
    window.addEventListener('load', () => {
      const perfData = performance.getEntriesByType('navigation')[0];
      if (perfData) {
        console.log(`Page loaded in ${Math.round(perfData.loadEventEnd - perfData.fetchStart)}ms`);
      }
    });
  }, []);

  return (
    <ErrorBoundary>
      <Router>
        <ScrollRestorer />
        <SmoothScrollEnhancer />
        <React.Suspense 
          fallback={
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
              background: '#2c2c48',
              color: '#ffffff'
            }}>
              <div className="logo-flash-container">
                <img
                  className="logo-flash"
                  src="/trade-it-ilogo-1@2x.png"
                  alt="Loading TradeIT Logo"
                  style={{ width: '120px', height: 'auto' }}
                />
              </div>
            </div>
          }
        >
          <main id="main-content">
            <Routes>
              <Route path="/" element={<CareersPage />} />
              <Route path="/careers/executive-secretary" element={<Navigate to="/careers/admin-assistant" replace />} />
              <Route path="/careers/:jobId" element={<CareersPage1 />} />
              <Route path="*" element={
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100vh',
                  background: '#2c2c48',
                  color: '#ffffff',
                  textAlign: 'center'
                }}>
                  <h1 style={{ color: '#585def' }}>Page Not Found</h1>
                  <p style={{ color: '#cbd5e1' }}>
                    The career opportunity you're looking for might have been filled or moved.
                  </p>
                  <a 
                    href="/" 
                    style={{
                      color: '#e194f4',
                      textDecoration: 'none',
                      marginTop: '16px'
                    }}
                  >
                    View All Open Positions
                  </a>
                </div>
              } />
            </Routes>
          </main>
        </React.Suspense>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
